// src/pages/outsourcing_management/InquiryFields.tsx
//
// The inquiry's fields, as one controlled grid. Both halves of InquiryDialog
// render this - raising an inquiry and editing one ask for the same things, and
// used to ask in two different layouts (a column of <Typography> elements used
// as containers on one side, a Grid on the other).
import { FunctionComponent, ReactNode } from "react";
import {
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { InquiryTypeEnum } from "types/InquiryTypes";
import { inquiryTypeLabel } from "./constants/inquiryStatusColors";
import { SupplierAutocomplete } from "components/Dashboard/supplierOption";
import { DELIVERY_METHODS, InquiryFormValues } from "./inquiryFormValues";

type PropsType = {
  values: InquiryFormValues;
  onChange: (patch: Partial<InquiryFormValues>) => void;
  /**
   * Type and title define what the inquiry is, so they are frozen once it has
   * left Draft - update_inquiry 409s the change. A Draft is still being
   * prepared, and so is an inquiry that does not exist yet.
   */
  lockCoreFields?: boolean;
  /** Per-field messages for values the backend cannot store as given. */
  errors?: Partial<Record<keyof InquiryFormValues, string>>;
  /**
   * Replaces the single supplier picker. An inquiry that exists is asked of a
   * shortlist, and the one supplier on it is a decision taken later - see
   * InquiryEditForm, which passes both controls in here. Raising a new one has
   * no id for candidates to hang off, so that form keeps the plain picker.
   */
  supplierSlot?: ReactNode;
  /**
   * Let the supplier picker fill itself in when exactly one supplier is
   * registered for the chosen type. Only the create half passes it - see
   * SupplierAutocomplete.autoSelectSingleMatch.
   */
  autoSelectSingleSupplier?: boolean;
};

const LOCKED_HELP = "Frozen once the inquiry leaves Draft";

const InquiryFields: FunctionComponent<PropsType> = ({
  values,
  onChange,
  lockCoreFields = false,
  errors = {},
  supplierSlot,
  autoSelectSingleSupplier = false,
}) => {
  return (
    // Two fields to a row at most: the edit half of InquiryDialog renders this
    // in the left of three columns, and a row of three fields there leaves the
    // Type and Deadline inputs too narrow to read their own values.
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          label="Title"
          value={values.title}
          onChange={(e) => onChange({ title: e.target.value })}
          inputProps={{ maxLength: 80 }}
          disabled={lockCoreFields}
          helperText={lockCoreFields ? LOCKED_HELP : undefined}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          select
          fullWidth
          size="small"
          label="Type"
          value={values.type}
          onChange={(e) => onChange({ type: e.target.value as InquiryTypeEnum })}
          disabled={lockCoreFields}
          helperText={lockCoreFields ? LOCKED_HELP : undefined}
        >
          {Object.values(InquiryTypeEnum).map((type) => (
            <MenuItem key={type} value={type}>
              {inquiryTypeLabel(type)}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="Deadline"
          InputLabelProps={{ shrink: true }}
          value={values.deadline ?? ""}
          onChange={(e) => onChange({ deadline: e.target.value || undefined })}
          error={!!errors.deadline}
          helperText={errors.deadline}
        />
      </Grid>

      {supplierSlot ? (
        // Full width: a shortlist is a list of rows, not a field.
        <Grid item xs={12}>
          {supplierSlot}
        </Grid>
      ) : (
        <Grid item xs={12} sm={6}>
          {/* The old form prefilled deliveryMethod and message from the
              partner on this change. It no longer does: on an existing inquiry
              that overwrites what a buyer wrote, and the two defaults it wrote
              are the ones the fields already start on. */}
          <SupplierAutocomplete
            value={values.partnerId}
            onChange={(partnerId) => onChange({ partnerId })}
            error={!!errors.partnerId}
            helperText={errors.partnerId}
            serviceType={values.type}
            autoSelectSingleMatch={autoSelectSingleSupplier}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <TextField
          select
          fullWidth
          size="small"
          label="Delivery method"
          value={values.deliveryMethod}
          onChange={(e) => onChange({ deliveryMethod: e.target.value })}
          error={!!errors.deliveryMethod}
          helperText={errors.deliveryMethod}
        >
          {DELIVERY_METHODS.map((method) => (
            <MenuItem key={method} value={method}>
              {method}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={3}
          label="Message to the supplier"
          value={values.message}
          onChange={(e) => onChange({ message: e.target.value })}
          error={!!errors.message}
          helperText={errors.message}
        />
      </Grid>

      {/* EE-240: what the supplier quoted. Free text on purpose - an answer is
          not always a clean number or date. */}
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">
          Supplier response
        </Typography>
      </Grid>
      {/* The prices are not asked for here - they are set line by line when
          the purchase order is placed (EE-253). */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Delivery time"
          value={values.deliveryTime}
          onChange={(e) => onChange({ deliveryTime: e.target.value })}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label="Internal notes"
          value={values.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
        />
      </Grid>
    </Grid>
  );
};

export default InquiryFields;
